import React from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Library, Check, Sparkles } from "lucide-react";
import { ENTITY_TYPES } from "@/lib/entities";

const IMP_LABEL = { 3: "Major", 2: "Supporting", 1: "Minor" };

export default function DetectReviewDialog({ detect, setDetect, onConfirm, busy }) {
  if (!detect) return null;
  const rows = detect.suggestions || [];
  const kept = rows.filter((r) => r.keep).length;
  const allOn = kept === rows.length && rows.length > 0;

  const toggle = (id, v) =>
    setDetect((d) => ({ ...d, suggestions: d.suggestions.map((r) => (r.id === id ? { ...r, keep: v } : r)) }));
  const toggleAll = (v) =>
    setDetect((d) => ({ ...d, suggestions: d.suggestions.map((r) => ({ ...r, keep: v })) }));

  return (
    <Dialog open onOpenChange={(v) => !v && !busy && setDetect(null)}>
      <DialogContent className="bg-popover max-w-xl" data-testid="detect-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Library className="w-5 h-5 text-primary" /> Review detected details
          </DialogTitle>
          <DialogDescription>
            {detect.source === "import" ? "Imported your manuscript. " : ""}
            Tick the details you want in your Codex. Nothing is added until you confirm.
          </DialogDescription>
        </DialogHeader>

        {rows.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">No new details found.</div>
        ) : (
          <>
            <div className="flex items-center justify-between px-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="detect-toggle-all">
                <Checkbox checked={allOn} onCheckedChange={(v) => toggleAll(!!v)} /> Select all
              </label>
              <span className="text-xs text-muted-foreground">{kept} of {rows.length} selected</span>
            </div>

            <ScrollArea className="max-h-[52vh] pr-3 -mr-3">
              <div className="space-y-2">
                {rows.map((r) => {
                  const meta = ENTITY_TYPES[r.type] || { label: r.type, color: "#64748b" };
                  return (
                    <div key={r.id} data-testid={`detect-row-${r.id}`}
                      className={`rounded-xl border p-3 transition-colors ${r.keep ? "border-border bg-card" : "border-border/50 bg-secondary/20 opacity-60"}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox checked={r.keep} onCheckedChange={(v) => toggle(r.id, !!v)}
                          data-testid={`detect-keep-${r.id}`} className="mt-1" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold truncate">{r.name}</span>
                            <span className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full"
                              style={{ backgroundColor: meta.color + "22", color: meta.color }}>{meta.label}</span>
                            {r.importance === 3 && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/15 text-primary font-medium">{IMP_LABEL[3]}</span>
                            )}
                            {!r.is_new && (
                              <span className="text-[10px] text-muted-foreground">· already in codex (will refresh)</span>
                            )}
                          </div>
                          {r.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.description}</p>}
                          {r.detail && <p className="text-[11px] text-primary/80 mt-0.5 font-mono-code">{r.detail}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setDetect(null)} disabled={busy} data-testid="detect-cancel">Cancel</Button>
              <Button onClick={onConfirm} disabled={busy || kept === 0} data-testid="detect-confirm-button" className="gap-2">
                {busy ? <Sparkles className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Add {kept} to codex
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
